import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusIcon from "@tSurya/Icons/MinusIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tabTitle = "tabTitle",
    desc = "desc",
    numberOfTabs = "numberOfTabs",
    faqBoxes = "faqBoxes",
    cssClass = "cssClass",
    image = "image",
    tableImage = "tableImage",
    constructionText = "constructionText",
    benefitsText = "benefitsText",
    characteristics = "characteristics",
    madeText = "madeText",
    imgLink = "imgLink",
    label = "label",
    name = "name",
    description = "description",
    tabdescription = "tabdescription",
    internalListLinks = "internalListLinks",
    internalListTitle = "internalListTitle",
    internalListDescription = "internalListDescription",
}

interface NameArray {
    name: string;
    tabdescription: string;
    faqBoxes: FAQModel[];
}

interface TabModel {
    fields: NameArray;
}
interface InternalListModel {
    fields: InternalListArray;
}

interface InternalListArray {
    internalListTitle: string;
    internalListDescription: string;
}

interface FAQArray {
    questions: string;
    answers: string;
    tableImage: string;
    constructionText: string;
    benefitsText: string;
    characteristics: string;
    madeText: string;
    imgLink: string;
    title: string;
    label: string;
    link: LinkFieldValue;
    description: string;
    internalListLinks: InternalListModel[];
}

interface FAQModel {
    fields: FAQArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabTitle]: string;
        [fields.desc]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.faqBoxes]: FAQModel[];
        [fields.internalListLinks]: InternalListModel;
        [fields.cssClass]: string;
        [fields.tabdescription]: string;
        [fields.name]: string;
    };
}

type Props = OwnProps;
export interface SurTabularDataStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    container?: InjectableCss;
    tabdesc?: InjectableCss;
    imageStyles?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    accordianContainer?: InjectableCss;
    headerBox?: DataTableHeaderProps;
    bodyContainer?: InjectableCss;
    accordianSection?: AccordionSectionPresentationProps;
    internalList?: InjectableCss;
    internalListTitle?: InjectableCss;
    internalListDescription?: InjectableCss;
    internalListContainer?: InjectableCss;
    ctaLink?: LinkProps;
    button?: InjectableCss;
    headerContainer?: DataTableHeadProps;
    bodyBox?: DataTableCellBaseProps;
    table?: DataTableProps;
    rowContainer?: DataTableRowProps;
    dataTableBody?: DataTableBodyProps;
}
export const tabularDataStyles: SurTabularDataStyles = {
    tabView: {
        css: css`
            background: ${getColor("common.background")};
            padding: 8px 12px;
            font-style: normal;
            min-width: auto;
            background-color: white;
            outline: 0;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            color: red;
            cursor: pointer;
            :focus {
                outline: 0;
            }
            /* border: 1px solid red; */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="true"] {
                border-bottom: 2px solid ${getColor("text.main")};
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 700;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    tabTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 600;
            font-size: 26px;
            line-height: 34px;
            text-align: center;
            color: ${getColor("text.main")};
            margin-bottom: 30px;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabdesc: {
        css: css`
            text-align: center;
            width: 100%;
            margin-bottom: 35px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 15px;
                        padding: 0px 20px;
                    `,
                    css`
                        margin-bottom: 15px;
                        padding: 0px 20px;
                    `,
                    css`
                        margin-bottom: 15px;
                        padding: 0px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    imageStyles: {
        css: css`
            max-width: 191px;
            /* max-height: 170px; */
            width: max-content;
            height: 100%;
            display: flex;
            img {
                object-fit: fill;
                aspect-ratio: 1/1;
                height: 100%;
                max-height: 196px !important;
            }
            /* ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 20px;
                    `,
                    css`
                        padding: 0px 20px;
                    `,
                    css`
                        padding: 0px 20px;
                    `,
                    null,
                    null,
                ])} */
        `,
    },
    container: {
        css: css`
            max-width: 955px;
            margin: 0 auto;
            > div[class^="TabGroupWrapper"] {
                padding: 0px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 4px;
                        `,
                        css`
                            padding: 4px;
                        `,
                        css`
                            padding: 4px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                gap: 21px;
                padding: 0px;
                z-index: 9;
                margin: 60px auto 35px;
                /* border: 1px solid blue; */
                > li > span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 400;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            tabContent: css`
                border: none;
                padding: 0px !important;
                max-width: 955px;
                /* border: 1px solid red; */
                margin: 0 auto;
                margin-bottom: 60px;
            `,
        },
    },
    accordianContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 20px 30px 20px;
                        overflow-x: scroll;
                    `,
                    css`
                        padding: 0px 20px 30px 20px;
                        overflow-x: scroll;
                    `,
                    css`
                        padding: 0px 20px 30px 20px;
                        overflow-x: scroll;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    accordianSection: {
        headerProps: {
            css: css`
                & button,
                & button:focus {
                    background-color: ${getColor("common.backgroundContrast")};

                    padding: 24px 20px;
                    border: 0;
                    > span:first-of-type {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 9px 20px 25px 40px;
                border: 0;
                &:not(dd[hidden]) {
                    display: flex;
                    display: flex;
                    align-items: center;
                }
                > span {
                    padding-left: 40px;
                    > p {
                        display: list-item;
                        margin-bottom: 0;
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.base};
                        color: ${getColor("text.accent")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        expandIconProps: {
            src: PlusIcon,
        },
        collapseIconProps: {
            src: MinusIcon,
        },
    },
    internalList: {
        css: css`
            max-width: 610px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            column-gap: 19px;
            margin-bottom: 58px;
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        margin-bottom: 20px;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        margin-bottom: 20px;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        margin-bottom: 20px;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    internalListContainer: {
        css: css`
            padding: 36px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 18px 20px;
                    `,
                    css`
                        padding: 18px 20px;
                    `,
                    css`
                        padding: 18px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    internalListTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: ${getColor("text.main")};
        `,
    },
    internalListDescription: {
        css: css`
            max-width: 393px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${getColor("text.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        text-align: center;
                    `,
                    css`
                        max-width: 100%;
                        text-align: center;
                    `,
                    css`
                        max-width: 100%;
                        text-align: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    button: {
        css: css`
            outline: 0;
            background-color: ${getColor("primary")};
            border: 1px solid ${getColor("primary")};
            padding: 10px 34px;
            width: max-content;
            max-width: 100%;
            /* display: block; */
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    null,
                    null,
                ])}
            span {
                display: block;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 24px;
                text-align: center;
                color: white !important;
                font-style: normal !important;
            }
        `,
    },
    headerContainer: {
        css: css`
            /* display: flex;
            align-items: center; */
            border: 1px solid ${getColor("common.border")};

            > th[class^="DataTableCellBase"] {
                background: ${getColor("common.backgroundContrast")} !important;
                background-color: ${getColor("common.backgroundContrast")} !important;
            }
        `,
    },
    headerBox: {
        tight: true,
        css: css`
            background: ${getColor("common.backgroundContrast")} !important;
            background-color: ${getColor("common.backgroundContrast")} !important;
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 14px;
                line-height: 22px;
                color: ${getColor("text.main")};
            }
            th {
                height: 100%;
                width: 100%;
                padding: 0px !important;
            }
        `,
    },
    bodyBox: {
        css: css`
            padding: 10px 14px !important;
            line-height: 22px !important;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            color: ${getColor("text.main")};
            span, p {
                font-family: inherit !important;
                color: inherit !important;
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                line-height: 22px;
            }
            white-space: break-spaces;
        `,
    },
    bodyContainer: {
        css: css`
            /* border: 1px solid green; */
        `,
    },
    table: {
        css: css`
            max-width: 955px;
            margin: 0 auto;
            table-layout: fixed;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        overflow-x: scroll;
                        table-layout: auto;
                    `,
                    css`
                        overflow-x: scroll;
                        table-layout: auto;
                    `,
                    css`
                        overflow-x: scroll;
                        table-layout: auto;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    rowContainer: {
        css: css`
            /* background: rgba(241, 239, 234, 0.498) !important; */
            background: rgba(248, 248, 248, 0.5) !important;
            td {
                text-align: left;
                background: rgba(248, 248, 248, 0.5) !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            min-width: 191px;
                        `,
                        css`
                            min-width: 191px;
                        `,
                        css`
                            min-width: 191px;
                        `,
                        null,
                        null,
                    ])}
                &:first-child {
                    padding: 0px !important;
                }
            }
        `,
    },
};

const styles = tabularDataStyles;

const SurRugGuideConstruction: React.FC<Props> = ({ fields, id }) => {
    const [expandedAccordian, setExpandedAccordian] = React.useState<number | null>(null);

    return (
        <StyledWrapper {...styles.container}>
            <Typography as="h2" {...styles.tabTitle}>
                {fields.name}
            </Typography>
            <Typography as="p" {...styles.tabdesc}>
                {fields.tabdescription}
            </Typography>

            <StyledWrapper>
                <StyledWrapper {...tabularDataStyles.accordianContainer}>
                    <DataTable {...styles.table}>
                        <DataTableHead {...styles.headerContainer}>
                            <DataTableHeader {...styles.headerBox}>{translate("")}</DataTableHeader>
                            <DataTableHeader {...styles.headerBox}>{translate("CONSTRUCTION")}</DataTableHeader>
                            <DataTableHeader {...styles.headerBox}>{translate("HOW IT’S MADE")}</DataTableHeader>
                            <DataTableHeader {...styles.headerBox}>{translate("BENEFITS")}</DataTableHeader>
                            <DataTableHeader {...styles.headerBox}>
                                {translate("OTHER CHARACTERISTICS")}
                            </DataTableHeader>
                        </DataTableHead>
                        {fields.faqBoxes.map((faqs: FAQModel, index: number) => {
                            return (
                                // eslint-disable-next-line react/no-array-index-key, react/jsx-key
                                <DataTableBody {...styles.dataTableBody}>
                                    <DataTableRow {...styles.rowContainer}>
                                        <DataTableCell {...styles.bodyBox}>
                                            <LazyImage
                                                src={faqs.fields.tableImage}
                                                {...styles.imageStyles}
                                                altText="Construction Image"
                                            />
                                        </DataTableCell>
                                        <DataTableCell {...styles.bodyBox}>
                                            {parse(faqs.fields.constructionText, parserOptions)}
                                        </DataTableCell>
                                        <DataTableCell {...styles.bodyBox}>{faqs.fields.madeText}</DataTableCell>
                                        <DataTableCell {...styles.bodyBox}>{faqs.fields.benefitsText}</DataTableCell>
                                        <DataTableCell {...styles.bodyBox}>{faqs.fields.characteristics}</DataTableCell>
                                    </DataTableRow>
                                </DataTableBody>
                            );
                        })}
                    </DataTable>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurRugGuideConstruction,
    definition: {
        group: "Common",
        displayName: "SurRugGuideConstruction",
        fieldDefinitions: [
            {
                name: fields.name,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.tabdescription,
                displayName: "Descriptioon",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.faqBoxes,
                displayName: "Construction Table Row Data",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                sortOrder: 2,
                fieldType: "General",
                fieldDefinitions: [
                    {
                        displayName: "Image",
                        name: "tableImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "constructionText",
                        displayName: "Construction",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: "madeText",
                        displayName: "Made Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "benefitsText",
                        displayName: "Benefits Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "characteristics",
                        displayName: "Characteristics",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;

// rugguideconstruction
